import {
  type GetShiftsResponse,
  type ShiftType,
  usePaginatedWorkerShifts,
  type UsePaginatedWorkerShiftsOptions,
} from "@src/appV2/redesign/WorkerShifts/api/usePaginatedWorkerShifts";
import { DEFAULT_TIMEZONE } from "@src/appV2/Shifts/Shift/constants";
import { type AxiosError } from "axios";
import { useMemo } from "react";

import { type SupportShift } from "../types";

interface UseSupportShiftsParams {
  workerId: string;
}

interface UseSupportShiftsResult {
  pastShifts: SupportShift[];
  upcomingShifts: SupportShift[];
  isLoading: boolean;
  isError: boolean;
  error: AxiosError | null;
  refetch: () => void;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
}

interface WorkplaceAttributes {
  name?: string;
  timezone?: string;
}

/**
 * Type guard to check if attributes have workplace structure.
 */
function hasWorkplaceAttributes(attributes: unknown): attributes is WorkplaceAttributes {
  if (!attributes || typeof attributes !== "object") {
    return false;
  }
  const record = attributes as Record<string, unknown>;
  return (
    (record.name === undefined || typeof record.name === "string") &&
    (record.timezone === undefined || typeof record.timezone === "string")
  );
}

/**
 * Finds workplace data from the included array by ID.
 */
function findWorkplace(
  workplaceId: string | undefined,
  included: GetShiftsResponse["included"]
): WorkplaceAttributes | undefined {
  if (!workplaceId || !included) {
    return undefined;
  }

  const workplace = included.find((item) => item.type === "workplace" && item.id === workplaceId);

  if (workplace?.type !== "workplace") {
    return undefined;
  }

  // Use type guard instead of unsafe assertion
  if (hasWorkplaceAttributes(workplace.attributes)) {
    return workplace.attributes;
  }

  return undefined;
}

/**
 * Resolves workplace name from shift relationships and included data.
 */
function resolveWorkplaceName(shift: ShiftType, included: GetShiftsResponse["included"]): string {
  const workplaceId = shift.relationships?.workplace?.data?.id;
  const workplace = findWorkplace(workplaceId, included);
  return workplace?.name ?? "Unknown Workplace";
}

/**
 * Resolves workplace timezone from shift relationships and included data.
 */
function resolveWorkplaceTimezone(
  shift: ShiftType,
  included: GetShiftsResponse["included"]
): string {
  const workplaceId = shift.relationships?.workplace?.data?.id;
  const workplace = findWorkplace(workplaceId, included);
  return workplace?.timezone ?? DEFAULT_TIMEZONE;
}

/**
 * Determines the completion status of a shift for display in the support flow.
 * Only handles active shifts (completed or upcoming).
 * Cancelled/deleted shifts are filtered out at the API level.
 */
function determineShiftStatus(isPast: boolean): SupportShift["status"] {
  return isPast ? "completed" : "upcoming";
}

/**
 * Converts dollars to cents with rounding to handle floating-point precision issues.
 */
function dollarsToCents(dollars: number | undefined): number | undefined {
  if (dollars === undefined) {
    return undefined;
  }
  return Math.round(dollars * 100);
}

/**
 * Transforms API shift data to SupportShift format.
 * Returns raw numeric values; formatting happens in the UI layer.
 */
function transformToSupportShift(
  shift: ShiftType,
  included: GetShiftsResponse["included"],
  isPast: boolean
): SupportShift {
  const { attributes } = shift;

  return {
    id: shift.id,
    workplaceName: resolveWorkplaceName(shift, included),
    startAt: attributes?.schedule?.startAt ?? "",
    endAt: attributes?.schedule?.endAt ?? "",
    timezone: resolveWorkplaceTimezone(shift, included),
    status: determineShiftStatus(isPast),
    // Raw hours as decimal - formatting happens in UI
    hoursWorkedInHours: attributes?.schedule?.durationWithoutBreaksInHours,
    // Convert dollars to cents once at the data layer to avoid float math issues
    paymentAmountInCents: dollarsToCents(attributes?.pay?.total),
    // Payment status is intentionally not set here.
    // Accurate payment status requires the payments API and is fetched
    // separately in the shift details view using useGetShiftPaymentStatus.
  };
}

/**
 * Hook to fetch shifts for the guided support flow.
 *
 * Fetches past shifts (within 30 days) and upcoming shifts,
 * transforming them into the SupportShift format for display.
 *
 * @param params - Contains the workerId to fetch shifts for
 * @param options - React Query options
 * @returns Combined result with past and upcoming shifts
 */
export function useSupportShifts(
  params: UseSupportShiftsParams,
  options: UsePaginatedWorkerShiftsOptions = {}
): UseSupportShiftsResult {
  const { workerId } = params;

  // Filter out deleted/cancelled shifts at the API level
  // Only fetch shifts where the worker remains assigned (deleted: false)
  const pastShiftsQuery = usePaginatedWorkerShifts(
    {
      workerId,
      timeline: "PAST",
      sortBy: "schedule.startAt",
      sortOrder: "desc",
      pageSize: 20,
      include: ["workplace"],
      filter: { deleted: false },
    },
    options
  );

  const upcomingShiftsQuery = usePaginatedWorkerShifts(
    {
      workerId,
      timeline: "FUTURE",
      sortBy: "schedule.startAt",
      sortOrder: "asc",
      pageSize: 20,
      include: ["workplace"],
      filter: { deleted: false },
    },
    options
  );

  const pastShifts = useMemo(() => {
    if (!pastShiftsQuery.data?.pages) {
      return [];
    }

    return pastShiftsQuery.data.pages.flatMap((page) =>
      page.data.map((shift) => transformToSupportShift(shift, page.included, true))
    );
  }, [pastShiftsQuery.data?.pages]);

  const upcomingShifts = useMemo(() => {
    if (!upcomingShiftsQuery.data?.pages) {
      return [];
    }

    return upcomingShiftsQuery.data.pages.flatMap((page) =>
      page.data.map((shift) => transformToSupportShift(shift, page.included, false))
    );
  }, [upcomingShiftsQuery.data?.pages]);

  const refetch = (): void => {
    void pastShiftsQuery.refetch();
    void upcomingShiftsQuery.refetch();
  };

  return {
    pastShifts,
    upcomingShifts,
    isLoading: pastShiftsQuery.isLoading || upcomingShiftsQuery.isLoading,
    isError: pastShiftsQuery.isError || upcomingShiftsQuery.isError,
    error: pastShiftsQuery.error ?? upcomingShiftsQuery.error,
    refetch,
    hasNextPage:
      (pastShiftsQuery.hasNextPage ?? false) || (upcomingShiftsQuery.hasNextPage ?? false),
    fetchNextPage: () => {
      if (pastShiftsQuery.hasNextPage) {
        void pastShiftsQuery.fetchNextPage();
      }

      if (upcomingShiftsQuery.hasNextPage) {
        void upcomingShiftsQuery.fetchNextPage();
      }
    },
    isFetchingNextPage:
      pastShiftsQuery.isFetchingNextPage || upcomingShiftsQuery.isFetchingNextPage,
  };
}
